import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LayoutIndex as Index } from "./layouts";
import { LayoutVehiclesListing as VehiclesListing } from "./layouts";
import { LayoutVehicleDetail as VehicleDetail } from "./layouts";
import { ScrollToTop } from "./components/ScrollToTop";
import { DynamicFavicon } from "./components/DynamicFavicon";
import { DynamicThemeColor } from "./components/DynamicThemeColor";
import { AppWrapper } from "./components/AppWrapper";
import { StoreSelectionModal } from "./components/StoreSelectionModal";
import { AppDataProvider, useAppData } from "./contexts/AppDataContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { StoreProvider } from "./contexts/StoreContext";
import { LayoutProvider } from "./contexts/LayoutContext";
import { Layout3ThemeSync } from "./layouts/layout-3/Layout3ThemeSync";
import { Layout4ThemeSync } from "./layouts/layout-4/Layout4ThemeSync";
import { Layout5ThemeSync } from "./layouts/layout-5/Layout5ThemeSync";
import { Layout6ThemeSync } from "./layouts/layout-6/Layout6ThemeSync";
import { Layout7ThemeSync } from "./layouts/layout-7/Layout7ThemeSync";
import { getWebsiteTemplate } from "./lib/websiteTemplate";
import { LoadingScreen } from "./components/LoadingScreen";

// Lazy load non-critical pages to reduce initial bundle size
const Contact = lazy(() => import("./layouts").then(m => ({ default: m.LayoutContact })));
const Services = lazy(() => import("./layouts").then(m => ({ default: m.LayoutServices })));
const Favorites = lazy(() => import("./layouts").then(m => ({ default: m.LayoutFavorites })));
const BannerVehicles = lazy(() => import("./layouts").then(m => ({ default: m.LayoutBannerVehicles })));
const PrivacyPolicy = lazy(() => import("./layouts").then(m => ({ default: m.LayoutPrivacyPolicy })));
const TermsOfUse = lazy(() => import("./layouts").then(m => ({ default: m.LayoutTermsOfUse })));
const NotFound = lazy(() => import("./layouts").then(m => ({ default: m.LayoutNotFound })));

const queryClient = new QueryClient();

const AppContent = () => {
  const { isInitialLoading, config } = useAppData();

  if (isInitialLoading) {
    return <LoadingScreen />;
  }

  return (
    <BrowserRouter>
      <DynamicFavicon />
      <DynamicThemeColor />
      <ScrollToTop />
      <StoreSelectionModal config={config} />
      <AppWrapper>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/estoque" element={<VehiclesListing />} />
          <Route path="/estoque/:id" element={<VehicleDetail />} />
          <Route path="/estoque/carro/*" element={<VehicleDetail />} />
          <Route path="/estoque/moto/*" element={<VehicleDetail />} />
          <Route path="/estoque/caminhao/*" element={<VehicleDetail />} />
          <Route 
            path="/favoritos" 
            element={
              <Suspense fallback={<LoadingScreen />}>
                <Favorites />
              </Suspense>
            } 
          />
          <Route 
            path="/destaques" 
            element={
              <Suspense fallback={<LoadingScreen />}>
                <BannerVehicles />
              </Suspense>
            } 
          />
          <Route 
            path="/contato" 
            element={
              <Suspense fallback={<LoadingScreen />}>
                <Contact />
              </Suspense>
            } 
          />
          <Route 
            path="/servicos" 
            element={
              <Suspense fallback={<LoadingScreen />}>
                <Services />
              </Suspense>
            } 
          />
          <Route 
            path="/politica-privacidade" 
            element={
              <Suspense fallback={<LoadingScreen />}>
                <PrivacyPolicy />
              </Suspense>
            } 
          />
          <Route 
            path="/termos-uso" 
            element={
              <Suspense fallback={<LoadingScreen />}>
                <TermsOfUse />
              </Suspense>
            } 
          />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route 
            path="*" 
            element={
              <Suspense fallback={<LoadingScreen />}>
                <NotFound />
              </Suspense>
            } 
          />
        </Routes>
      </AppWrapper>
    </BrowserRouter>
  );
};

const AppLayoutWrapper = () => {
  const { config } = useAppData();
  const websiteTemplate = getWebsiteTemplate(config?.websiteTemplate);

  return (
    <LayoutProvider websiteTemplate={websiteTemplate}>
      <Layout3ThemeSync />
      <Layout4ThemeSync />
      <Layout5ThemeSync />
      <Layout6ThemeSync />
      <Layout7ThemeSync />
      <Toaster />
      <Sonner />
      <AppContent />
    </LayoutProvider>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AppDataProvider>
        <ThemeProvider>
          <StoreProvider>
            <AppLayoutWrapper />
          </StoreProvider>
        </ThemeProvider>
      </AppDataProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
